import React, { useState, useEffect, useContext } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Fab,
  Grid,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  Paper,
  TextField,
  Typography,
  withStyles
} from '@material-ui/core';
import Add from '@material-ui/icons/Add';
import Delete from '@material-ui/icons/Delete';
import Edit from '@material-ui/icons/Edit';
import Check from '@material-ui/icons/Check';
import Close from '@material-ui/icons/Close';
import { appStyles } from '../../styles';
import FlightOptionsContext from '../Session/flightOptionsContext';
import ConfirmDialog from '../Forms/ConfirmDialog';

const TaskConfigurationPanel = ({ firebase, classes }) => {
  const flightOptions = useContext(FlightOptionsContext);
  const [subsections, setSubsections] = useState([]);
  
  // Modal State
  const [openSubDialog, setOpenSubDialog] = useState(false);
  const [newSubTitle, setNewSubTitle] = useState('');
  const [openTaskDialog, setOpenTaskDialog] = useState(false);
  const [newTaskLabel, setNewTaskLabel] = useState('');
  const [activeSubIndex, setActiveSubIndex] = useState(null);

  // Edit State
  const [editingType, setEditingType] = useState(null); // 'subsection' | 'task'
  const [editingSubIndex, setEditingSubIndex] = useState(null);
  const [editingTaskIndex, setEditingTaskIndex] = useState(null);
  const [editValue, setEditValue] = useState('');

  // Confirmation State
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [confirmTitle, setConfirmTitle] = useState('');
  const [confirmText, setConfirmText] = useState('');
  const [confirmAction, setConfirmAction] = useState(() => () => {});

  useEffect(() => {
    if (flightOptions && flightOptions.sortieTaskDefaults) {
      setSubsections(flightOptions.sortieTaskDefaults);
    }
  }, [flightOptions]);

  const updateAndSave = (newSubsections) => {
    setSubsections(newSubsections);
    firebase.sortieTaskDefaults().set({ subsections: newSubsections });
  };

  const handleOpenAddSubsection = () => {
    setNewSubTitle('');
    setOpenSubDialog(true);
  };

  const handleConfirmAddSubsection = () => {
    const newSubsections = [
      ...subsections,
      { title: newSubTitle, tasks: [] }
    ];
    updateAndSave(newSubsections);
    setOpenSubDialog(false);
  };

  const handleRemoveSubsection = (index) => {
    setConfirmTitle('Delete Subsection');
    setConfirmText('Are you sure you want to delete this subsection? All tasks within it will be removed.');
    setConfirmAction(() => () => {
      const newSubsections = subsections.filter((_, i) => i !== index);
      updateAndSave(newSubsections);
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  const handleEditSubsection = (index) => {
    setEditingType('subsection');
    setEditingSubIndex(index);
    setEditValue(subsections[index].title);
  };

  const handleEditTask = (subIndex, taskIndex) => {
    setEditingType('task');
    setEditingSubIndex(subIndex);
    setEditingTaskIndex(taskIndex);
    setEditValue(subsections[subIndex].tasks[taskIndex].label);
  };

  const handleCancelEdit = () => {
    setEditingType(null);
    setEditingSubIndex(null);
    setEditingTaskIndex(null);
    setEditValue('');
  };

  const handleSaveEdit = () => {
    if (editingType === 'subsection') {
      const newSubsections = subsections.map((sub, i) => 
        i === editingSubIndex ? { ...sub, title: editValue } : sub
      );
      updateAndSave(newSubsections);
    } else if (editingType === 'task') {
      const newSubsections = subsections.map((sub, i) => {
        if (i === editingSubIndex) {
          const newTasks = sub.tasks.map((task, j) => 
            j === editingTaskIndex ? { ...task, label: editValue } : task
          );
          return { ...sub, tasks: newTasks };
        }
        return sub;
      });
      updateAndSave(newSubsections);
    }
    handleCancelEdit();
  };

  const handleOpenAddTask = (subIndex) => {
    setActiveSubIndex(subIndex);
    setNewTaskLabel('');
    setOpenTaskDialog(true);
  };

  const handleConfirmAddTask = () => {
    const newId = `task_${Date.now()}`;
    const newSubsections = subsections.map((sub, i) => 
      i === activeSubIndex ? { ...sub, tasks: [...sub.tasks, { id: newId, label: newTaskLabel }] } : sub
    );
    updateAndSave(newSubsections);
    setOpenTaskDialog(false);
  };

  const handleRemoveTask = (subIndex, taskIndex) => {
    setConfirmTitle('Delete Task');
    setConfirmText('Are you sure you want to delete this task?');
    setConfirmAction(() => () => {
      const newSubsections = subsections.map((sub, i) => {
        if (i === subIndex) {
          const newTasks = [...sub.tasks];
          newTasks.splice(taskIndex, 1);
          return { ...sub, tasks: newTasks };
        }
        return sub;
      });
      updateAndSave(newSubsections);
      setConfirmOpen(false);
    });
    setConfirmOpen(true);
  };

  return (
    <Paper className={classes.paper}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Sortie Task Defaults</Typography>
        <Fab className={`${classes.fab} ${classes.fabAdd}`}
             color="secondary"
             aria-label="add-subsection"
             size={"small"}
             onClick={handleOpenAddSubsection}
        >
          <Add />
        </Fab>
      </Box>
      <Grid container spacing={2}>
        {subsections.map((subsection, subIndex) => {
          const isEditingSub = editingType === 'subsection' && editingSubIndex === subIndex;
          
          return (
            <Grid item xs={12} key={subIndex}>
              <Paper variant="outlined" style={{ padding: '16px' }}>
                <Box display="flex" flexDirection="column" mb={1}>
                  <Box display="flex" alignItems="center" justifyContent="space-between">
                    <TextField
                      label="Subsection Title"
                      value={isEditingSub ? editValue : subsection.title}
                      onChange={(e) => setEditValue(e.target.value)}
                      disabled={!isEditingSub}
                      fullWidth
                    />
                    {!isEditingSub && (
                      <Box display="flex">
                        <IconButton aria-label="edit-subsection" onClick={() => handleEditSubsection(subIndex)}>
                          <Edit />
                        </IconButton>
                        <IconButton aria-label="delete-subsection" onClick={() => handleRemoveSubsection(subIndex)}>
                          <Delete />
                        </IconButton>
                      </Box>
                    )}
                  </Box>
                  {isEditingSub && (
                    <Box display="flex" justifyContent="flex-end" mt={1}>
                      <IconButton aria-label="cancel-edit" onClick={handleCancelEdit} color="secondary">
                        <Close />
                      </IconButton>
                      <IconButton aria-label="save-edit" onClick={handleSaveEdit} color="primary">
                        <Check />
                      </IconButton>
                    </Box>
                  )}
                </Box>
                <List>
                  {subsection.tasks.map((task, taskIndex) => {
                    const isEditingTask = editingType === 'task' && editingSubIndex === subIndex && editingTaskIndex === taskIndex;
                    
                    return (
                      <ListItem key={task.id} style={{ display: 'block' }}>
                        <Box display="flex" alignItems="center" width="100%">
                          <TextField
                            label="Task Label"
                            value={isEditingTask ? editValue : task.label}
                            onChange={(e) => setEditValue(e.target.value)}
                            disabled={!isEditingTask}
                            fullWidth
                          />
                          {!isEditingTask && (
                            <ListItemSecondaryAction>
                              <IconButton aria-label="edit-task" edge="end" onClick={() => handleEditTask(subIndex, taskIndex)} style={{ marginRight: '8px' }}>
                                <Edit />
                              </IconButton>
                              <IconButton aria-label="delete-task" edge="end" onClick={() => handleRemoveTask(subIndex, taskIndex)}>
                                <Delete />
                              </IconButton>
                            </ListItemSecondaryAction>
                          )}
                        </Box>
                        {isEditingTask && (
                          <Box display="flex" justifyContent="flex-end" mt={1}>
                            <IconButton aria-label="cancel-edit" onClick={handleCancelEdit} color="secondary">
                              <Close />
                            </IconButton>
                            <IconButton aria-label="save-edit" onClick={handleSaveEdit} color="primary">
                              <Check />
                            </IconButton>
                          </Box>
                        )}
                      </ListItem>
                    );
                  })}
                  <Button
                    color="primary"
                    startIcon={<Add />}
                    onClick={() => handleOpenAddTask(subIndex)}
                  >
                    Add Task
                  </Button>
                </List>
              </Paper>
            </Grid>
          );
        })}
      </Grid>

      {/* Add Subsection Dialog */}
      <Dialog open={openSubDialog} onClose={() => setOpenSubDialog(false)}>
        <DialogTitle>Add New Subsection</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="new-subsection-title"
            label="New Subsection Title"
            fullWidth
            value={newSubTitle}
            onChange={(e) => setNewSubTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenSubDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAddSubsection} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      {/* Add Task Dialog */}
      <Dialog open={openTaskDialog} onClose={() => setOpenTaskDialog(false)}>
        <DialogTitle>Add New Task</DialogTitle>
        <DialogContent>
          <TextField
            autoFocus
            margin="dense"
            id="new-task-label"
            label="New Task Label"
            fullWidth
            value={newTaskLabel}
            onChange={(e) => setNewTaskLabel(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenTaskDialog(false)} color="secondary">
            Cancel
          </Button>
          <Button onClick={handleConfirmAddTask} color="primary">
            Add
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirmOpen}
        title={confirmTitle}
        text={confirmText}
        onCancel={() => setConfirmOpen(false)}
        onConfirm={confirmAction}
      />
    </Paper>
  );
};

export default withStyles(appStyles)(TaskConfigurationPanel);
